// Adapted from custom-report's dashboardEditor parser/serializer. This builds an
// editing tree only; all numeric evaluation remains in the Cowork backend.
export const KPI_AGGREGATES = ["sum", "avg", "min", "max", "count"] as const
export const KPI_FUNCTIONS = ["sum", "avg", "min", "max", "count", "safe_divide", "round", "abs", "if_else"] as const
const arity: Record<string, number[]> = { safe_divide: [2], round: [1, 2], abs: [1], if_else: [3] }
const comparisons = [">", ">=", "<", "<=", "==", "!="]
const numeric = /^(?:0[xX][\da-fA-F](?:_?[\da-fA-F])*|0[bB][01](?:_?[01])*|0[oO][0-7](?:_?[0-7])*|(?:\d(?:_?\d)*(?:\.(?:\d(?:_?\d)*)?)?|\.\d(?:_?\d)*)(?:[eE][+-]?\d(?:_?\d)*)?)/

export type KpiFormulaNode =
  | { type: "number"; value: string }
  | { type: "literal"; value: string }
  | { type: "aggregate"; name: string; column: string | null; quoted: boolean }
  | { type: "binary"; operator: string; left: KpiFormulaNode; right: KpiFormulaNode }
  | { type: "unary"; operator: string; operand: KpiFormulaNode }
  | { type: "logic"; operator: string; values: KpiFormulaNode[] }
  | { type: "call"; name: string; args: KpiFormulaNode[] }

export class KpiFormulaSyntaxError extends Error {
  constructor(readonly position: number) { super("invalid_expression") }
}

export function parseKpiFormula(expression: string): KpiFormulaNode {
  const tokens: Array<{ kind: string; value: string; offset: number }> = []
  let offset = 0
  let position = 0
  let parentheses = 0
  const fail = (): never => { throw new KpiFormulaSyntaxError((tokens[position]?.offset ?? offset) + 1) }
  if (!expression.trim() || expression.length > 1000) fail()
  while (offset < expression.length) {
    const rest = expression.slice(offset)
    if (/^\s/.test(rest)) {
      if (rest[0] === "\n" && parentheses === 0 && tokens.length && rest.replace(/#[^\n]*/g, "").trim()) fail()
      offset++
      continue
    }
    if (rest[0] === "#") { const end = rest.indexOf("\n"); offset += end === -1 ? rest.length : end; continue }
    if (rest.startsWith("\\\n") || rest.startsWith("\\\r\n")) { offset += rest.startsWith("\\\r") ? 3 : 2; continue }
    if (rest[0] === "`") {
      const end = rest.indexOf("`", 1)
      if (end < 1 || !rest.slice(1, end).trim()) fail()
      tokens.push({ kind: "field", value: rest.slice(1, end), offset })
      offset += end + 1
      continue
    }
    const number = rest.match(numeric)?.[0]
    if (number) {
      if ((/^0[\d_]+$/.test(number) && /[1-9]/.test(number)) || !Number.isFinite(Number(number.replace(/_/g, "")))) fail()
      tokens.push({ kind: "number", value: number, offset })
      offset += number.length
      continue
    }
    const name = rest.match(/^[\p{L}_][\p{L}\p{N}\p{M}_]*/u)?.[0]
    const value = name || rest.match(/^(?:>=|<=|==|!=|[()+\-*/<>,])/)?.[0]
    if (!value) fail()
    if (value === "(" && ++parentheses > 100) fail()
    if (value === ")") parentheses--
    tokens.push({ kind: name ? "name" : "symbol", value: value!, offset })
    if (tokens.length > 300) fail()
    offset += value!.length
  }
  const peek = () => tokens[position]?.value
  const consume = (value: string) => { if (peek() !== value) fail(); return tokens[position++] }
  function primary(): KpiFormulaNode {
    if (peek() === "(") { consume("("); const node = logicalOr(); consume(")"); return node }
    const token = tokens[position++]
    if (!token) return fail()
    if (token.kind === "number") return { type: "number", value: token.value }
    if (["True", "False", "None"].includes(token.value)) return { type: "literal", value: token.value }
    if (token.kind !== "name" || peek() !== "(") return fail()
    const name = token.value
    const normalized = name.toLowerCase()
    consume("(")
    if (KPI_AGGREGATES.some((item) => item === normalized)) {
      if (normalized === "count") { consume(")"); return { type: "aggregate", name, column: null, quoted: false } }
      const field = tokens[position++]
      if (!field || !["field", "name"].includes(field.kind) || (field.kind === "name" && ["and", "or", "not", "True", "False", "None"].includes(field.value))) return fail()
      if (peek() === ",") consume(",")
      consume(")")
      return { type: "aggregate", name, column: field.value, quoted: field.kind === "field" }
    }
    if (!Object.hasOwn(arity, normalized)) return fail()
    const args: KpiFormulaNode[] = []
    if (peek() !== ")") {
      args.push(logicalOr())
      while (peek() === ",") { consume(","); if (peek() === ")") break; args.push(logicalOr()) }
    }
    consume(")")
    if (!arity[normalized].includes(args.length)) return fail()
    return { type: "call", name, args }
  }
  function unary(depth = 0): KpiFormulaNode {
    if (depth > 20) return fail()
    if (["+", "-"].includes(peek())) return { type: "unary", operator: tokens[position++].value, operand: unary(depth + 1) }
    return primary()
  }
  function binary(lower: () => KpiFormulaNode, operators: string[]): KpiFormulaNode {
    let left = lower()
    while (operators.includes(peek())) left = { type: "binary", operator: tokens[position++].value, left, right: lower() }
    return left
  }
  const product = () => binary(unary, ["*", "/"])
  const sum = () => binary(product, ["+", "-"])
  function comparison(): KpiFormulaNode {
    const left = sum()
    if (!comparisons.includes(peek())) return left
    const node: KpiFormulaNode = { type: "binary", operator: tokens[position++].value, left, right: sum() }
    if (comparisons.includes(peek())) return fail()
    return node
  }
  function logicalNot(depth = 0): KpiFormulaNode {
    if (depth > 20) return fail()
    if (peek() === "not") { consume("not"); return { type: "unary", operator: "not", operand: logicalNot(depth + 1) } }
    return comparison()
  }
  function logic(lower: () => KpiFormulaNode, operator: string): KpiFormulaNode {
    const values = [lower()]
    while (peek() === operator) { consume(operator); values.push(lower()) }
    return values.length === 1 ? values[0] : { type: "logic", operator, values }
  }
  const logicalAnd = () => logic(logicalNot, "and")
  const logicalOr = () => logic(logicalAnd, "or")
  const tree = logicalOr()
  if (position !== tokens.length) fail()
  function metrics(node: KpiFormulaNode): { count: number; depth: number } {
    if (node.type === "number" || node.type === "literal") return { count: 1, depth: 1 }
    if (node.type === "aggregate") return { count: node.column === null ? 3 : 5, depth: 3 }
    const children = node.type === "binary" ? [node.left, node.right] : node.type === "unary" ? [node.operand] : node.type === "logic" ? node.values : node.args
    const values = children.map(metrics)
    return { count: (node.type === "call" ? 3 : 2) + values.reduce((total, item) => total + item.count, 0), depth: 1 + Math.max(node.type === "call" ? 2 : 1, ...values.map((item) => item.depth)) }
  }
  const complexity = metrics(tree)
  if (complexity.count + 1 > 100 || complexity.depth + 1 > 20) fail()
  return tree
}

export function serializeKpiFormula(tree: KpiFormulaNode): string {
  function write(node: KpiFormulaNode, depth = 0): string {
    if (!node || depth > 20) throw new KpiFormulaSyntaxError(1)
    const child = (value: KpiFormulaNode) => write(value, depth + 1)
    if (node.type === "number") {
      const value = node.value.replace(/^[+-]/, "")
      if (!value || numeric.exec(value)?.[0] !== value) throw new KpiFormulaSyntaxError(1)
      return node.value
    }
    if (node.type === "literal") return node.value
    if (node.type === "aggregate") {
      if (node.name.toLowerCase() === "count") return `${node.name}()`
      if (!node.column?.trim() || node.column.includes("`")) throw new KpiFormulaSyntaxError(1)
      return `${node.name}(${node.quoted ? `\`${node.column}\`` : node.column})`
    }
    if (node.type === "binary") return `(${child(node.left)} ${node.operator} ${child(node.right)})`
    if (node.type === "unary") return `(${node.operator} ${child(node.operand)})`
    if (node.type === "logic") return `(${node.values.map(child).join(` ${node.operator} `)})`
    return `${node.name}(${node.args.map(child).join(", ")})`
  }
  const expression = write(tree)
  parseKpiFormula(expression)
  return expression
}

export function formulaTemplate(kind: string): KpiFormulaNode {
  const aggregate = (): KpiFormulaNode => ({ type: "aggregate", name: "sum", column: "", quoted: true })
  if (kind === "ratio") return { type: "call", name: "safe_divide", args: [aggregate(), aggregate()] }
  if (kind === "difference") return { type: "binary", operator: "-", left: aggregate(), right: aggregate() }
  if (kind === "conditional") return { type: "call", name: "if_else", args: [
    { type: "binary", operator: ">", left: aggregate(), right: { type: "number", value: "0" } },
    { type: "number", value: "1" }, { type: "number", value: "0" },
  ] }
  if (kind === "count") return { type: "aggregate", name: "count", column: null, quoted: false }
  return aggregate()
}

export function formulaCompletions(expression: string, cursor: number, columns: string[], selectionEnd = cursor) {
  const before = expression.slice(0, cursor)
  const quoted = (before.match(/`/g) || []).length % 2 === 1
  const start = quoted ? before.lastIndexOf("`") : cursor - (before.match(/[\p{L}\p{N}_]*$/u)?.[0].length || 0)
  const closing = quoted ? expression.indexOf("`", cursor) : -1
  const end = Math.max(selectionEnd, quoted ? closing < 0 ? cursor : closing + 1 : cursor + (expression.slice(cursor).match(/^[\p{L}\p{N}_]*/u)?.[0].length || 0))
  const query = expression.slice(start + (quoted ? 1 : 0), cursor).toLowerCase()
  const options = [
    ...(!quoted ? KPI_FUNCTIONS.map((name) => ({ label: name, text: expression[end] === "(" ? name : `${name}()`, kind: "function" as const, caret: expression[end] === "(" ? name.length : name === "count" ? 7 : name.length + 1 })) : []),
    ...columns.filter((name) => !name.includes("`")).map((name) => ({ label: name, text: `\`${name}\``, kind: "column" as const, caret: name.length + 2 })),
  ]
  return { start, end, options: options.filter((option) => option.label.toLowerCase().includes(query)) }
}
